/**
 * AlmaReactiveText
 *
 * Mouse-repulsion effect for text and floating SVG letters.
 * Based on the designer prototype at experiments.bielkeyang.com/ml/alma-typo/
 *
 * Two modes:
 *   [data-alma-reactive]  — HTML text: splits into per-word spans, repels on X axis
 *   [data-alma-float]     — SVG letters: idle floating animation + mouse repulsion on X/Y
 */

const DEFAULTS = {
  // Repulsion physics
  ySpread: 24, // Gaussian falloff spread for Y-axis influence
  xSpread: 44, // Exponential decay spread for X-axis influence
  basePush: 8, // Base push in px
  maxLocalPush: 16, // Max additional push based on proximity
  // Spring dynamics
  lerpHover: 0.08, // Lerp speed during active hover
  lerpSettle: 0.045, // Lerp speed when settling back
  // Floating (SVG only)
  floatAmplitude: 3, // Max px displacement for idle float
  floatSpeed: 0.0008, // Speed of sine wave
  // SVG letter sizing
  svgLetterScale: 1.6, // Global multiplier for SVG letter size (1 = 1em)
  // SVG repulsion strength (multiplier, 1 = same as text, lower = softer)
  svgRepulsionStrength: 0.7,
  // Threshold to stop animation
  restThreshold: 0.01,
}

export default class AlmaReactiveText {
  constructor(app, opts = {}) {
    this.app = app
    this.opts = { ...DEFAULTS, ...opts }
    this.mouse = { x: -9999, y: -9999 }
    this.isHovering = false
    this.running = false
    this.reactiveGroups = []
    this.floatGroups = []

    this.hasHover = window.matchMedia('(hover: hover)').matches
    this.init()

  }

  init() {
    // Set up floating SVG containers (needed for normalization on all devices)
    const floatEls = document.querySelectorAll('[data-alma-float]')
    floatEls.forEach(el => this.setupFloatingLetters(el))

    // Normalize SVG sizes so letters are visually the same height across all SVGs
    this.normalizeFloatSvgs()

    // Recalc on resize (debounced)
    let resizeTimer
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => this.normalizeFloatSvgs(), 150)
    })

    // Start floating animation regardless of mouse
    if (this.floatGroups.length > 0) {
      this.startLoop()
    }

    // Only set up mouse interactions on hover-capable devices
    if (!this.hasHover) return

    // Set up reactive text containers
    const reactiveEls = document.querySelectorAll('[data-alma-reactive]')
    reactiveEls.forEach(el => this.setupReactiveText(el))

    // Global mouse tracking
    document.addEventListener('pointermove', e => {
      this.mouse.x = e.clientX
      this.mouse.y = e.clientY
      this.isHovering = true
      this.startLoop()
    })

    document.addEventListener('pointerleave', () => {
      this.isHovering = false
    })
  }

  /**
   * Split text content into individual word spans for CSS transform targeting.
   */
  setupReactiveText(container) {
    const textEl = container
    // Get the raw text, handling CKEditor output (may have <p> tags)
    const paragraphs = textEl.querySelectorAll('p')
    const targets = paragraphs.length > 0 ? paragraphs : [textEl]

    const words = []

    targets.forEach(p => {
      // Walk child nodes to preserve <br> elements while splitting text into word spans
      const fragment = document.createDocumentFragment()

      p.childNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'BR') {
          fragment.appendChild(document.createElement('br'))
          return
        }

        const text = (node.textContent || '').trim()
        if (!text) return

        text.split(/\s+/).forEach((w, i, arr) => {
          const span = document.createElement('span')
          span.className = 'alma-word'
          span.textContent = w
          fragment.appendChild(span)
          if (i < arr.length - 1) {
            fragment.appendChild(document.createTextNode(' '))
          }
        })
      })

      p.innerHTML = ''
      p.appendChild(fragment)

      // Track each word span
      const spans = p.querySelectorAll('.alma-word')
      spans.forEach(span => {
        words.push({
          el: span,
          currentX: 0,
          targetX: 0,
        })
      })
    })

    if (words.length > 0) {
      this.reactiveGroups.push({ container, words })
    }
  }

  /**
   * Set up SVG child elements for floating and repulsion.
   */
  setupFloatingLetters(container) {
    const svg = container.querySelector('svg')
    if (!svg) return

    // Find all direct child elements with IDs (letters can be <path>, <g>, or other SVG elements)
    const groups = svg.querySelectorAll('[id]')
    if (groups.length === 0) return

    const letters = []
    groups.forEach((g, i) => {
      // Cache original BBox position before any transforms
      const bbox = g.getBBox()
      letters.push({
        el: g,
        index: i,
        // Original center in viewBox coordinates (never changes)
        origCx: bbox.x + bbox.width / 2,
        origCy: bbox.y + bbox.height / 2,
        currentX: 0,
        currentY: 0,
        targetX: 0,
        targetY: 0,
        floatPhase: Math.random() * Math.PI * 2, // Random phase offset
      })
    })

    this.floatGroups.push({ container, svg, letters })
  }

  /**
   * Size all float SVGs so individual letters render at a consistent
   * visual height (1em of the parent block's font-size).
   * Each SVG gets explicit width/height styles computed from its
   * viewBox and median letter path height.
   */
  normalizeFloatSvgs() {
    if (this.floatGroups.length === 0) return

    // 1. Compute per-SVG ideal scale (makes letters uniform)
    const svgData = this.floatGroups.map(({ svg, letters, container }) => {
      const vb = svg.viewBox?.baseVal
      if (!vb || !vb.width || !vb.height) return null

      const heights = letters
        .filter(l => l.el.tagName !== 'g')
        .map(l => {
          try { return l.el.getBBox().height } catch (_e) { return 0 }
        })
        .filter(h => h > 0)
        .sort((a, b) => a - b)

      if (heights.length === 0) return null
      const medianH = heights[Math.floor(heights.length / 2)]

      const fontSize = parseFloat(getComputedStyle(container).fontSize) || 30
      const targetLetterH = fontSize * this.opts.svgLetterScale

      // Per-SVG scale for uniform letter height
      const scale = targetLetterH / medianH

      // Max scale that fits container width
      const containerWidth = container.getBoundingClientRect().width
      const maxScale = containerWidth / vb.width

      // How much this SVG needs to shrink to fit (1 = fits, <1 = needs reduction)
      const fitRatio = maxScale / scale

      return { svg, vb, scale, fitRatio }
    }).filter(Boolean)

    if (svgData.length === 0) return

    // 2. Find the tightest fit ratio — if any SVG overflows, reduce ALL by that ratio
    const globalReduction = Math.min(1, ...svgData.map(d => d.fitRatio))

    // 3. Apply per-SVG scale × global reduction
    svgData.forEach(({ svg, vb, scale }) => {
      const finalScale = scale * globalReduction
      svg.style.width = vb.width * finalScale + 'px'
      svg.style.height = vb.height * finalScale + 'px'
    })
  }

  /**
   * Main animation loop — runs conditionally.
   */
  startLoop() {
    if (this.running) return
    this.running = true
    this.tick()
  }

  tick() {
    let needsUpdate = false
    const now = performance.now()

    // Update reactive text groups
    this.reactiveGroups.forEach(group => {
      const updated = this.updateReactiveWords(group)
      if (updated) needsUpdate = true
    })

    // Update floating SVG groups (always animate for float)
    this.floatGroups.forEach(group => {
      const updated = this.updateFloatingLetters(group, now)
      if (updated) needsUpdate = true
    })

    if (needsUpdate || this.isHovering || this.floatGroups.length > 0) {
      requestAnimationFrame(() => this.tick())
    } else {
      this.running = false
    }
  }

  /**
   * Calculate repulsion for reactive HTML word spans.
   */
  updateReactiveWords(group) {
    const { words } = group
    let anyMoving = false
    const lerp = this.isHovering ? this.opts.lerpHover : this.opts.lerpSettle

    words.forEach(word => {
      const rect = word.el.getBoundingClientRect()
      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2

      // Distance from mouse
      const dx = this.mouse.x - cx
      const dy = this.mouse.y - cy

      // Gaussian falloff for Y-axis influence
      const yInfluence = Math.exp(-Math.pow(dy / (this.opts.ySpread * 4), 2))
      // Exponential decay for X-axis
      const xInfluence = Math.exp(-Math.abs(dx) / (this.opts.xSpread * 4))

      // Combined influence
      const influence = yInfluence * xInfluence

      // Push direction (away from mouse on X axis)
      const pushDir = dx < 0 ? 1 : -1
      const pushMagnitude = (this.opts.basePush + this.opts.maxLocalPush * influence) * influence

      word.targetX = this.isHovering ? pushDir * pushMagnitude : 0

      // Lerp toward target
      word.currentX += (word.targetX - word.currentX) * lerp

      // Apply transform
      if (Math.abs(word.currentX) > this.opts.restThreshold) {
        word.el.style.transform = `translateX(${word.currentX}px)`
        anyMoving = true
      } else {
        word.currentX = 0
        word.el.style.transform = ''
      }
    })

    return anyMoving
  }

  /**
   * Calculate floating + repulsion for SVG letter groups.
   */
  updateFloatingLetters(group, now) {
    const { svg, letters } = group
    const lerp = this.isHovering ? this.opts.lerpHover : this.opts.lerpSettle
    const svgRect = svg.getBoundingClientRect()

    letters.forEach(letter => {
      // Idle floating animation (always active)
      const floatX =
        Math.sin(now * this.opts.floatSpeed + letter.floatPhase) * this.opts.floatAmplitude
      const floatY =
        Math.cos(now * this.opts.floatSpeed * 0.7 + letter.floatPhase + 1) *
        this.opts.floatAmplitude *
        0.6

      // Convert cached original position to viewport space
      const scaleX = svgRect.width / (svg.viewBox?.baseVal?.width || svgRect.width)
      const scaleY = svgRect.height / (svg.viewBox?.baseVal?.height || svgRect.height)
      const cx = svgRect.left + letter.origCx * scaleX
      const cy = svgRect.top + letter.origCy * scaleY

      // Mouse repulsion (both axes)
      const dx = this.mouse.x - cx
      const dy = this.mouse.y - cy
      const dist = Math.sqrt(dx * dx + dy * dy)
      const influence = Math.exp(-dist / 150) // 150px influence radius

      let repelX = 0
      let repelY = 0
      if (this.isHovering && influence > 0.01) {
        const pushMag = this.opts.basePush * this.opts.svgRepulsionStrength * influence
        const angle = Math.atan2(dy, dx)
        repelX = -Math.cos(angle) * pushMag
        repelY = -Math.sin(angle) * pushMag
      }

      letter.targetX = floatX + repelX
      letter.targetY = floatY + repelY

      // Lerp
      letter.currentX += (letter.targetX - letter.currentX) * lerp
      letter.currentY += (letter.targetY - letter.currentY) * lerp

      // Apply SVG transform
      letter.el.setAttribute(
        'transform',
        `translate(${letter.currentX.toFixed(2)} ${letter.currentY.toFixed(2)})`,
      )
    })

    // Floating always counts as "moving"
    return true
  }
}
